import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, View } from 'react-native';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { CoverflexCardDetailsHorizontalBack } from '@/common/components/Illustrations/CoverflexCardDetailsHorizontal/Back/CoverflexCardDetailsHorizontalBack';
import { CoverflexCardDetailsHorizontalFront } from '@/common/components/Illustrations/CoverflexCardDetailsHorizontal/Front/CoverflexCardDetailsHorizontalFront';
import { Stack } from '@/common/components/Stack/Stack';
import { MARKET } from '@/common/constants/markets';
import { useMarket } from '@/common/hooks/useMarket';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import { useRequestCardDetails } from '@/common/integrations/menhir/employee/card/card.hooks';
import {
  ICard,
  TNormalizedCardDetails,
} from '@/common/integrations/menhir/employee/card/card.types';
import { notify } from '@/common/utils/toast/toast';
import { styles } from './CardPreview.styles';

export function CardPreview({ card }: TCardPreviewProps) {
  const { t } = useTranslation();

  const { slug } = useMarket();
  const {
    features: { ff_digital_cards },
  } = useFeatures();

  const [cardFlipped, setCardFlipped] = useState(false);
  const [cardDetails, setCardDetails] = useState<TNormalizedCardDetails>();
  const { mutateAsync: requestCardDetails, isLoading } =
    useRequestCardDetails();
  const [flipAnimation] = useState(new Animated.Value(0));

  const canShowDetails = card.status === 'active';

  useEffect(() => {
    if (card.status === 'locked') {
      setCardFlipped(false);
      Animated.timing(flipAnimation, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }).start();
    }
  }, [card.status, flipAnimation]);

  useEffect(() => {
    const getCardDetails = async () => {
      try {
        const requestedCardDetails = await requestCardDetails({
          id: card.id,
          status: card.status,
          requiresEncryption: slug !== MARKET.PT,
        });
        setCardDetails(requestedCardDetails);
      } catch (error) {
        notify({
          message: t('dashboard.card.details.request_details_error'),
          type: 'error',
        });
      }
    };

    if (ff_digital_cards && cardFlipped) {
      setCardDetails(undefined);
      if (card.status !== 'inactive') {
        getCardDetails();
      }
    }
  }, [
    card.id,
    card.status,
    cardFlipped,
    ff_digital_cards,
    requestCardDetails,
    slug,
    t,
  ]);

  const flipCard = () => {
    const toValue = cardFlipped ? 0 : 1;
    setCardFlipped(!cardFlipped);
    Animated.timing(flipAnimation, {
      toValue,
      duration: 800,
      useNativeDriver: true,
    }).start();
  };

  const frontAnimatedStyle = {
    transform: [
      {
        rotateY: flipAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: ['0deg', '180deg'],
        }),
      },
    ],
    opacity: flipAnimation.interpolate({
      inputRange: [0, 0.49, 0.51, 1],
      outputRange: [1, 1, 0, 0],
    }),
    position: 'absolute' as const,
  };

  const backAnimatedStyle = {
    transform: [
      {
        rotateY: flipAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: ['180deg', '360deg'],
        }),
      },
    ],
    opacity: flipAnimation.interpolate({
      inputRange: [0, 0.49, 0.51, 1],
      outputRange: [0, 0, 1, 1],
    }),
    position: 'absolute' as const,
  };

  return (
    <Stack gap={'lg'} justify="center" align="center">
      <View className={styles.cardWrapper()}>
        <Animated.View style={frontAnimatedStyle}>
          <CoverflexCardDetailsHorizontalFront card={card} />
        </Animated.View>

        {canShowDetails && (
          <Animated.View style={backAnimatedStyle}>
            <CoverflexCardDetailsHorizontalBack
              card={card}
              details={cardDetails}
            />
          </Animated.View>
        )}
      </View>

      {canShowDetails && (
        <Button
          variant="tertiary"
          size="sm"
          onPress={flipCard}
          loading={isLoading}
          icon={<Icon icon="repeat" />}
        >
          {cardFlipped
            ? t('dashboard.card.details.show')
            : t('dashboard.card.details.hide')}
        </Button>
      )}
    </Stack>
  );
}

export type TCardPreviewProps = {
  card: ICard;
};
